import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { Users, Award, Clock, LogOut, Eye, FileStack, TrendingUp } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CertificateCard from "@/components/CertificateCard";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
};

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [moduleStats, setModuleStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [exportingAll, setExportingAll] = useState(false);
  const [exportTarget, setExportTarget] = useState(null);
  const exportRef = useRef(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, sRes, mRes] = await Promise.all([
        api.get("/admin/participants"),
        api.get("/admin/stats"),
        api.get("/admin/module-stats"),
      ]);
      setParticipants(pRes.data);
      setStats(sRes.data);
      setModuleStats(mRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const handleExportAllCertificates = async () => {
    const certifiedList = participants.filter((p) => p.certified && p.certificate_id);
    if (certifiedList.length === 0) {
      toast.error("No certified participants to export yet.");
      return;
    }
    setExportingAll(true);
    try {
      let pdf = null;
      for (const p of certifiedList) {
        flushSync(() => setExportTarget(p));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 80));
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(exportRef.current, { scale: 2, backgroundColor: "#ffffff" });
        const imgData = canvas.toDataURL("image/png");
        if (!pdf) {
          pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
        } else {
          pdf.addPage([canvas.width, canvas.height], "landscape");
        }
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      }
      pdf.save(`DENSO_All_Certificates_${Date.now()}.pdf`);
      toast.success(`Exported ${certifiedList.length} certificate${certifiedList.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error("Something went wrong generating the certificates PDF. Please try again.");
    } finally {
      setExportingAll(false);
      setExportTarget(null);
    }
  };

  const filteredParticipants = useMemo(() => {
    if (filter === "completed") return participants.filter((p) => p.certified);
    if (filter === "incomplete") return participants.filter((p) => !p.certified);
    return participants;
  }, [participants, filter]);

  const statCards = stats
    ? [
        { key: "total_participants", label: "Total Participants", value: stats.total_participants, icon: Users },
        { key: "total_certified", label: "Certified", value: stats.total_certified, icon: Award },
        { key: "total_attempts", label: "Module Quiz Attempts", value: stats.total_attempts, icon: Clock },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 h-16 flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-slate-900">DENSO Training Admin</h1>
          <p className="text-xs text-slate-500">Signed in as {admin?.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportAllCertificates}
            disabled={exportingAll}
            data-testid={ADMIN.exportAllCertificatesButton}
            className="text-denso-red border-denso-red/30 hover:bg-red-50"
          >
            <FileStack className="h-4 w-4" /> {exportingAll ? "Generating..." : "Download All Certificates"}
          </Button>
          <Button variant="outline" onClick={handleLogout} data-testid={ADMIN.logoutButton}>
            <LogOut className="h-4 w-4" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        <div className="grid sm:grid-cols-3 gap-4">
          {statCards.map((s) => (
            <div key={s.key} data-testid={ADMIN.statCard(s.key)} className="bg-white rounded-md border border-slate-200 p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-red-50 flex items-center justify-center">
                <s.icon className="h-5 w-5 text-denso-red" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-denso-red" />
            <div>
              <h2 className="font-semibold text-slate-800">Module Difficulty Insight</h2>
              <p className="text-xs text-slate-500">Modules requiring more attempts on average may need clearer content</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead>Trainees Attempted</TableHead>
                  <TableHead>Trainees Passed</TableHead>
                  <TableHead>Total Attempts</TableHead>
                  <TableHead>Avg Attempts to Pass</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && moduleStats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-400 py-8">No quiz attempts yet.</TableCell>
                  </TableRow>
                )}
                {moduleStats.map((m) => (
                  <TableRow key={m.module_id} data-testid={ADMIN.moduleStatRow(m.module_id)}>
                    <TableCell className="font-medium">{m.module_title}</TableCell>
                    <TableCell>{m.participants_attempted}</TableCell>
                    <TableCell>{m.participants_passed}</TableCell>
                    <TableCell>{m.total_attempts}</TableCell>
                    <TableCell>
                      {m.avg_attempts_to_pass != null ? (
                        <Badge className={m.avg_attempts_to_pass > 1.5 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
                          {m.avg_attempts_to_pass}
                        </Badge>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-semibold text-slate-800">Participants</h2>
              <p className="text-xs text-slate-500">Registration, module progress, login time and certificate status per participant</p>
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid={ADMIN.filterSelect} className="w-48">
                <SelectValue placeholder="Filter participants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Participants</SelectItem>
                <SelectItem value="completed">Completed Course</SelectItem>
                <SelectItem value="incomplete">Did Not Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Date Registered</TableHead>
                <TableHead>Modules Complete</TableHead>
                <TableHead>Quiz Attempts</TableHead>
                <TableHead>Login Time</TableHead>
                <TableHead>Cert Issued</TableHead>
                <TableHead>Cert Downloaded</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-400 py-8">Loading...</TableCell>
                </TableRow>
              )}
              {!loading && filteredParticipants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-400 py-8">No participants match this filter.</TableCell>
                </TableRow>
              )}
              {filteredParticipants.map((p) => (
                <TableRow key={p.id} data-testid={ADMIN.participantRow(p.id)}>
                  <TableCell className="font-medium">
                    {p.name} {p.surname}
                    <p className="text-xs text-slate-400">{p.mobile}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDateTime(p.created_at)}</TableCell>
                  <TableCell>{p.modules_completed_count}/{p.total_modules}</TableCell>
                  <TableCell>{p.total_quiz_attempts}</TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDateTime(p.last_login_at)}</TableCell>
                  <TableCell>
                    <Badge className={p.certified ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                      {p.certified ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      data-testid={ADMIN.certDownloadedBadge(p.id)}
                      className={p.certificate_downloaded ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}
                    >
                      {p.certificate_downloaded ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" data-testid={ADMIN.viewAttemptsButton(p.id)} onClick={() => navigate(`/admin/participants/${p.id}`)}>
                      <Eye className="h-3.5 w-3.5" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      </div>

      {/* Off-screen render target used to rasterize each certificate during bulk export */}
      <div style={{ position: "fixed", top: 0, left: "-9999px", zIndex: -1 }}>
        {exportTarget && (
          <CertificateCard
            ref={exportRef}
            name={exportTarget.name}
            surname={exportTarget.surname}
            certificateId={exportTarget.certificate_id}
            dateStr={
              exportTarget.certified_at
                ? new Date(exportTarget.certified_at).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })
                : ""
            }
          />
        )}
      </div>
    </div>
  );
}
